import React from 'react';
import { CircleCheck, CircleX, ExternalLink, Upload } from 'lucide-react';
import { Badge } from 'src/shared/components/ui/badge';
import useBoolean from 'src/shared/hooks/useBoolean';
import UploadDataModal from 'src/shared/components/upload-data-modal/UploadDataModal';
import BookCallModal from 'src/app/portfolio/components/book-call-modal/BookCallModal';

export enum DetectionBadgeType {
    Detected = 'detected',
    Undetected = 'undetected',
    UploadData = 'uploadData',
    BookCall = 'bookCall',
}

const badgeClasses = {
    [DetectionBadgeType.Detected]: {
        badgeClass: 'text-xs text-black font-normal bg-green-0 hover:bg-green-0 border border-green-20',
        icon: <CircleCheck data-testid='detected-icon' data-icon='inline-start' className='w-[13.5px] h-[13.5px] mr-1 text-green-100' />,
        label: 'Detected',
        isDefaultVariant: true,
    },
    [DetectionBadgeType.Undetected]: {
        badgeClass: 'text-xs text-black font-normal bg-red-0 hover:bg-red-0 border border-red-20',
        icon: <CircleX data-testid='undetected-icon' data-icon='inline-start' className='w-[13.5px] h-[13.5px] mr-1 text-red-100' />,
        label: 'Undetected',
        isDefaultVariant: true,
    },
    [DetectionBadgeType.UploadData]: {
        badgeClass: 'border border-none font-normal cursor-pointer',
        icon: <Upload data-testid='upload-data-icon' data-icon='inline-end' className='w-[16.5px] h-[16.5px] ml-1' />,
        label: 'Upload data',
        isDefaultVariant: false,
    },
    [DetectionBadgeType.BookCall]: {
        badgeClass: 'border border-none font-normal cursor-pointer',
        icon: <ExternalLink data-testid='book-call-icon' data-icon='inline-end' className='w-[16.5px] h-[16.5px] ml-1' />,
        label: 'Book a call',
        isDefaultVariant: false,
    },
};

type DetectionBadgeProps = {
    type: DetectionBadgeType;
    iconPosition: 'start' | 'end';
};

export const DetectionBadge = ({ type, iconPosition }: DetectionBadgeProps) => {
    const [showImportModal, setShowImportModal] = useBoolean(false);
    const [showBookCallModal, setShowBookCallModal] = useBoolean(false);
    const { badgeClass, icon, label, isDefaultVariant } = badgeClasses[type];

    const onClick = () => {
        if (type === DetectionBadgeType.BookCall) {
            setShowBookCallModal.on();
        } else if (type === DetectionBadgeType.UploadData) {
            setShowImportModal.on();
        }
    };

    return (
        <>
            <Badge variant={isDefaultVariant ? 'default' : null} className={badgeClass} onClick={onClick}>
                {iconPosition === 'start' ? icon : null}
                {label}
                {iconPosition === 'end' ? icon : null}
            </Badge>
            {showImportModal && <UploadDataModal onClose={setShowImportModal.off} />}
            {showBookCallModal && <BookCallModal onClose={setShowBookCallModal.off} />}
        </>
    );
};

export default DetectionBadge;
